import CommonModalBase from '@/components/common/CommonModal'
import CustomInput from '@/components/Returns/CustomInput'
import CustomSelect from '@/components/Returns/CustomSelect'
import { getEntityCountryDropdown } from '@/multi-territory/mt-services/mt-entity-api'
import type {
  MTEntityFormCountryOption as CountryOption,
  MTEntityFormData as EntityFormData,
  MTEntityFormErrors as FormErrors,
  MTAdminEntityFormProps,
} from '@/multi-territory/mt-types/mt-admin'
import {
  expandCharClass,
  FieldSchemaConfig,
  validateField,
  validateWithSchema,
} from '@/multi-territory/mt-utils/mt-form-schema'
import { Box, Button, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

const CommonModal = CommonModalBase as any
const EntityInput = CustomInput as any
const EntitySelect = CustomSelect as any

// Fallback example when the API supplies neither uin_example nor a describable
// uin_regex. Examples are otherwise fully backend-driven (no per-country map),
// so they can never drift out of sync with the validation regex.
const DEFAULT_UIN_EXAMPLE = 'ex. 1234567890'

// Build a sample value from a simple char-class regex like
// "^[A-Z]{5}[0-9]{4}[A-Z]{1}$" so any onboarded country gets a meaningful
// example without a hardcoded entry. Returns '' if the regex is not a simple
// sequence of quantified character classes.
const buildExampleFromRegex = (regex?: string): string => {
  if (!regex) return ''
  // Unwrap a single outer (?:...) / (...) group, then take the FIRST branch so
  // alternation regexes (e.g. "^[0-9]{9}[A-Z]$|^[0-9]{10}[A-Z]$") still produce
  // a sample that matches a valid accepted format. Per-branch ^/$ are stripped.
  const outer = regex.trim().replace(/^\^/, '').replace(/\$$/, '')
  const unwrapped = /^\((?:\?:)?(.*)\)$/.exec(outer)?.[1] ?? outer
  const firstBranch = unwrapped.split('|')[0] ?? ''
  const body = firstBranch.trim().replace(/^\^+/, '').replace(/\$+$/, '')
  const tokenRe = /\[([^\]]+)\]\{(\d+)(?:,\d+)?\}|\[([^\]]+)\]/g
  let sample = ''
  let match: RegExpExecArray | null
  let matchedAny = false
  let lastIndex = 0
  while ((match = tokenRe.exec(body)) !== null) {
    // Bail out if there is content between tokens we did not understand.
    if (match.index !== lastIndex) return ''
    matchedAny = true
    const cls = match[1] ?? match[3] ?? ''
    const count = match[2] ? parseInt(match[2], 10) : 1
    // Build a realistic-looking run: digits increment 1,2,3…9,0; letters
    // cycle A,B,C… — so examples read like a real UIN (e.g. "123456789A")
    // instead of a repeated character ("111111111A").
    // Only pick characters the class actually allows, so "[1-9]" yields a
    // digit 1-9 (never 0 or a placeholder "X") and the example always matches.
    const allowed = expandCharClass(cls)
    if (!allowed || !allowed.length) return ''
    const digits = '1234567890'.split('').filter(c => allowed.includes(c))
    const upper = allowed.filter(c => /[A-Z]/.test(c))
    const lower = allowed.filter(c => /[a-z]/.test(c))
    const pool = upper.length ? upper : lower.length ? lower : digits
    for (let i = 0; i < count; i++) {
      sample += pool[i % pool.length]
    }
    lastIndex = tokenRe.lastIndex
  }
  if (!matchedAny || lastIndex !== body.length) return ''
  return sample ? `ex. ${sample}` : ''
}

export function MTAdminEntityForm({
  open,
  onClose,
  editingEntity = null,
  onSubmit,
}: MTAdminEntityFormProps) {
  // State
  const sidebarCountryCode = useSelector(
    (state: any) => state.country.countryCode
  )
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([])
  const [formData, setFormData] = useState<EntityFormData>({
    entityName: '',
    country: '',
    uniqueIdentificationNumber: '',
  })
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [formLoading, setFormLoading] = useState(false)

  // Fetch country list on mount
  useEffect(() => {
    fetchCountryList()
  }, [])

  // Reset form when modal opens or editingEntity changes
  useEffect(() => {
    if (open) {
      if (editingEntity) {
        setFormData({
          entityName: (editingEntity.entityName as string) || '',
          country:
            (editingEntity.countryCode as string) ||
            sidebarCountryCode ||
            countryOptions[0]?.countryCode ||
            '',
          uniqueIdentificationNumber:
            (editingEntity.uniqueIdentificationNumber as string) || '',
        })
      } else {
        setFormData({
          entityName: '',
          // Do NOT preselect a country: the Unique identification number
          // validation depends on the chosen country, so the user must pick one.
          country: '',
          uniqueIdentificationNumber: '',
        })
      }
      setFormErrors({})
    }
  }, [open, editingEntity])

  const fetchCountryList = async () => {
    const resp = await getEntityCountryDropdown()

    if (resp.success && Array.isArray(resp.data) && resp.data.length > 0) {
      // Map snake_case API response to camelCase CountryOption
      const mapped: CountryOption[] = resp.data.map(item => ({
        uuid: item.uuid,
        countryCode: item.country_code,
        countryName: item.country_name,
        identifierName: item.identifier_name || '',
        uinRegex: item.uin_regex || '',
        uinExample: item.uin_example?.trim() || '',
      }))

      setCountryOptions(mapped)
      // Intentionally do not auto-select a country here. The country must be
      // chosen by the user so the UIN field/validation uses the right regex.
    }
  }

  // Get the currently selected country option
  const getSelectedCountry = (): CountryOption | undefined =>
    countryOptions.find(opt => opt.countryCode === formData.country)

  // Get the unique identification label based on selected country
  const getUniqueIdLabel = (): string => {
    const selectedCountry = getSelectedCountry()
    if (selectedCountry?.identifierName) {
      return `Unique identification number (${selectedCountry.identifierName})`
    }
    return 'Unique identification number'
  }

  // Get placeholder/example based on the selected country: API-supplied
  // uin_example first, then a sample derived from uin_regex, then a default.
  const getUniqueIdPlaceholder = (): string => {
    const selectedCountry = getSelectedCountry()
    // 1) Backend-supplied example. Only used if it actually matches the
    //    regex, so a stale example can never contradict the validation.
    const apiExample = selectedCountry?.uinExample
    if (apiExample) {
      const regex = selectedCountry?.uinRegex
      let matches = true
      if (regex) {
        try {
          matches = new RegExp(`^(?:${regex})$`).test(apiExample)
        } catch {
          matches = false
        }
      }
      if (matches) return `ex. ${apiExample}`
    }
    // 2) Otherwise derive a sample from the country's uin_regex so the example
    //    always matches the actual validation pattern.
    const derived = buildExampleFromRegex(selectedCountry?.uinRegex)
    if (derived) {
      return derived
    }
    // 3) Fallback.
    return DEFAULT_UIN_EXAMPLE
  }

  // Build Zod schema config based on selected country
  const getEntityFormSchemaConfig = (): FieldSchemaConfig[] => {
    const selectedCountry = getSelectedCountry()
    const uinLabel =
      selectedCountry?.identifierName || 'Unique identification number'
    const fields: FieldSchemaConfig[] = [
      {
        name: 'entityName',
        label: 'Entity name',
        required: true,
        maxLength: 255,
      },
      { name: 'country', label: 'Country', required: true },
      {
        name: 'uniqueIdentificationNumber',
        label: uinLabel,
        required: true,
        regex: selectedCountry?.uinRegex || undefined,
        regexExample: getUniqueIdPlaceholder(),
      },
    ]
    return fields
  }

  const handleInputChange = (field: keyof EntityFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear error for the field on change
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
      }))
    }

    // Real-time validation for unique identification number
    if (field === 'uniqueIdentificationNumber' && value.trim()) {
      const schemaConfig = getEntityFormSchemaConfig()
      const uinConfig = schemaConfig.find(
        f => f.name === 'uniqueIdentificationNumber'
      )
      if (uinConfig) {
        const error = validateField(uinConfig, value)
        if (error) {
          setFormErrors(prev => ({
            ...prev,
            uniqueIdentificationNumber: error,
          }))
        }
      }
    }
  }

  const handleCountryChange = (event: { target: { value: string } }) => {
    const selectedCode = event.target.value
    setFormData(prev => ({
      ...prev,
      country: selectedCode,
      uniqueIdentificationNumber: '',
    }))
    setFormErrors(prev => ({
      ...prev,
      country: '',
      uniqueIdentificationNumber: '',
    }))
  }

  const validateForm = (): boolean => {
    const schemaConfig = getEntityFormSchemaConfig()
    const dataToValidate = {
      entityName: formData.entityName.trim(),
      country: formData.country,
      uniqueIdentificationNumber: formData.uniqueIdentificationNumber.trim(),
    }
    const errors = validateWithSchema(schemaConfig, dataToValidate)
    if (errors) {
      setFormErrors(errors as FormErrors)
      return false
    }
    setFormErrors({})
    return true
  }

  const handleFormSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setFormLoading(true)
    try {
      // Enrich form data with country UUID and name for save API
      const selectedCountry = getSelectedCountry()
      const enrichedData: EntityFormData = {
        ...formData,
        countryUuid: selectedCountry?.uuid ?? '',
        countryName: selectedCountry?.countryName ?? '',
      }
      await onSubmit(enrichedData)
      handleModalClose()
    } catch (error: unknown) {
      const errMsg =
        error instanceof Error ? error.message : 'An unexpected error occurred'
      setFormErrors({ general: errMsg })
    } finally {
      setFormLoading(false)
    }
  }

  const handleModalClose = () => {
    setFormData({
      entityName: '',
      country: '',
      uniqueIdentificationNumber: '',
    })
    setFormErrors({})
    onClose()
  }

  return (
    <CommonModal
      open={open}
      onClose={handleModalClose}
      title={editingEntity ? 'Edit entity' : 'Add entity'}
      maxWidth="sm"
    >
      <>
        {/* Mandatory fields note */}
        <Typography
          variant="body2"
          sx={{
            color: 'var(--forbrand-error)',
            mb: 4,
            fontSize: '14px',
          }}
        >
          (*) Fields are mandatory
        </Typography>

        {/* Form container */}
        <Box>
          <form onSubmit={handleFormSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <EntityInput
                label="Entity name"
                required
                placeholder="Entity Name"
                value={formData.entityName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('entityName', e.target.value)
                }
                error={!!formErrors.entityName}
                helperText={formErrors.entityName}
                maxLength={255}
                fullWidth
              />

              <EntitySelect
                label="Country"
                required
                value={formData.country}
                onChange={handleCountryChange}
                options={countryOptions}
                getOptionLabel={(option: CountryOption) => option.countryName}
                getOptionValue={(option: CountryOption) => option.countryCode}
                placeholder="Select country"
                error={!!formErrors.country}
                helperText={formErrors.country}
                disabled={!!editingEntity}
                fullWidth
              />

              <EntityInput
                label={getUniqueIdLabel()}
                required
                placeholder={getUniqueIdPlaceholder()}
                value={formData.uniqueIdentificationNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(
                    'uniqueIdentificationNumber',
                    e.target.value.toUpperCase()
                  )
                }
                error={!!formErrors.uniqueIdentificationNumber}
                helperText={formErrors.uniqueIdentificationNumber}
                // UIN validation is country-specific (per-country uin_regex),
                // so keep this disabled until a country is selected.
                disabled={!formData.country}
                fullWidth
              />
            </Box>
            {formErrors.general && (
              <Typography variant="body2" color="error" sx={{ my: 1 }}>
                {formErrors.general}
              </Typography>
            )}
          </form>
        </Box>

        {/* Save button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingTop: '32px',
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formLoading}
            onClick={handleFormSubmit}
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
              textTransform: 'none',
              px: '17px',
              py: '8px',
              fontSize: '16px',
              '&:disabled': {
                backgroundColor: '#ccc',
              },
            }}
          >
            {formLoading ? 'Saving...' : editingEntity ? 'Update' : 'Save'}
          </Button>
        </Box>
      </>
    </CommonModal>
  )
}
